import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { detailsFaqs } from '@/mocks/pricingDetailsData';

export default function FAQSection() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: detailsFaqs.map((faq, idx) => ({
      '@type': 'Question',
      name: lang === 'en' ? t(`pricingDetails.faq.${idx}.q`) : faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: lang === 'en' ? t(`pricingDetails.faq.${idx}.a`) : faq.answer,
      },
    })),
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      <div className="max-w-[800px] mx-auto px-8">
        <div className="text-center mb-14">
          {String(t('pricingDetails.faq.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('pricingDetails.faq.title')}
          </h2>
          {String(t('pricingDetails.faq.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
        </div>

        <div className="space-y-3">
          {detailsFaqs.map((faq, idx) => (
            <div
              key={idx}
              className="border border-background-200/60 bg-background-50"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
              >
                <span className="text-[16px] text-foreground-800 tracking-[0.04em] font-medium pr-4">
                  {lang === 'en' ? t(`pricingDetails.faq.${idx}.q`) : faq.question}
                </span>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`text-accent-500 text-sm transition-transform duration-300 ${
                      openIndex === idx ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === idx ? 'max-h-[300px]' : 'max-h-0'
                }`}
              >
                <p className="px-5 pb-5 text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed">
                  {lang === 'en' ? t(`pricingDetails.faq.${idx}.a`) : faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[14px] text-foreground-400 tracking-[0.04em] mt-8">
          {t('pricingDetails.faq.contactNote')}
          <a href="/contact" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors mx-1">
            {t('pricingDetails.faq.contactLink')}
          </a>
          {t('pricingDetails.faq.contactSuffix')}
        </p>
      </div>
    </section>
  );
}
